import { readonly, ref } from 'vue'
import { FAIR_TOAST_LIFE_MS } from '@/ui/tokens/fair-toast.data'

export interface FairToastOptions {
  /** Plain message, or text before an optional link. */
  message: string
  /** Optional trailing link label (Figma: «Список сравнения»). */
  linkLabel?: string
  onLinkClick?: () => void
  /** Auto-dismiss ms; default FAIR_TOAST_LIFE_MS (3000). */
  life?: number
  /** Leading check icon; default true (Figma toast). */
  showIcon?: boolean
}

export interface FairToastItem {
  id: number
  message: string
  linkLabel?: string
  onLinkClick?: () => void
  showIcon: boolean
}

const toasts = ref<FairToastItem[]>([])
let nextId = 1
const timers = new Map<number, ReturnType<typeof setTimeout>>()

function clearTimer(id: number) {
  const timer = timers.get(id)
  if (timer != null) {
    clearTimeout(timer)
    timers.delete(id)
  }
}

function dismiss(id: number) {
  clearTimer(id)
  toasts.value = toasts.value.filter((item) => item.id !== id)
}

function clearAll() {
  for (const id of timers.keys()) {
    clearTimer(id)
  }
  toasts.value = []
}

function show(options: FairToastOptions) {
  const rawMessage = options.message ?? ''
  const message = rawMessage.trimStart()
  if (!message.trim()) return

  clearAll()

  const id = nextId++
  const life = options.life ?? FAIR_TOAST_LIFE_MS
  const linkLabel = options.linkLabel?.trim() || undefined
  // Keep trailing space on message when a link follows (Figma: «Товар добавлен в » + link).
  const displayMessage = linkLabel
    ? (message.endsWith(' ') ? message : `${message.trimEnd()} `)
    : message.trim()

  const item: FairToastItem = {
    id,
    message: displayMessage,
    linkLabel,
    onLinkClick: options.onLinkClick,
    showIcon: options.showIcon !== false,
  }

  toasts.value = [item]

  if (life > 0) {
    timers.set(
      id,
      setTimeout(() => {
        dismiss(id)
      }, life),
    )
  }
}

/** Module singleton — host + callers share one queue. */
export function useFairToast() {
  return {
    toasts: readonly(toasts),
    show,
    dismiss,
    clearAll,
  }
}
